import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/api';
import { GroupWorkoutPlanStatus } from '../types';
import { AlertCircle, Clock, Download, Loader2, Target } from 'lucide-react';

const formatDateTime = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const GroupTrainingPlans: React.FC = () => {
  const [status, setStatus] = useState<GroupWorkoutPlanStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const workoutPlanState = status?.workout_plan ?? null;
  const workoutPlanMaterial = workoutPlanState?.material ?? null;
  const isGenerating = workoutPlanState?.status === 'queued' || workoutPlanState?.status === 'generating';
  const isPendingReview = workoutPlanState?.status === 'pending_review';

  const fetchStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getGroupWorkoutPlanStatus();
      setStatus(data);
    } catch (err: any) {
      console.error('Failed to load workout plan status:', err);
      setError(err?.message || 'Failed to load workout plan');
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  // Auto-refresh while generating or pending review
  useEffect(() => {
    if (!isGenerating && !isPendingReview) return;
    const interval = window.setInterval(() => {
      fetchStatus();
    }, 5000);
    return () => window.clearInterval(interval);
  }, [isGenerating, isPendingReview]);

  const generatedAtLabel = useMemo(
    () => formatDateTime(workoutPlanMaterial?.generated_at ?? null),
    [workoutPlanMaterial?.generated_at],
  );

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Training Plan</h1>
        <p className="mt-2 text-gray-600">
          View and download your coach-approved workout plan.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {!status?.onboarding?.exists ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Target className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Complete onboarding to generate your plan</h2>
              <p className="text-gray-600 mt-1">
                Once you submit onboarding, your plan will be generated and reviewed by your coach.
              </p>
              <div className="mt-4">
                <Link
                  to="/onboarding-group"
                  className="btn-primary inline-flex items-center"
                  tabIndex={0}
                  aria-label="Start onboarding"
                >
                  Start onboarding
                </Link>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Generating state */}
          {isGenerating && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Generating your workout plan</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Your plan is being created. This page will update automatically.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pending review state */}
          {isPendingReview && (
            <div className="bg-amber-50 rounded-lg shadow-sm border border-amber-200 p-6">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-amber-500" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Your plan is being reviewed</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Your coach is reviewing your workout plan. You will be emailed once it is approved.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Failed state */}
          {workoutPlanState?.status === 'failed' && (
            <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-red-500" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Plan generation failed</h2>
                  <p className="text-sm text-red-600 mt-1">
                    {workoutPlanState.error || 'Something went wrong. Your coach has been notified.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Approved / ready state — show download */}
          {workoutPlanState?.status === 'ready' && workoutPlanMaterial && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Your latest workout plan</h2>
                  <p className="text-sm text-gray-500 mt-1">Last generated: {generatedAtLabel}</p>
                  {workoutPlanMaterial.title && (
                    <p className="text-sm text-gray-700 mt-2">
                      <span className="font-medium">Title:</span> {workoutPlanMaterial.title}
                    </p>
                  )}
                </div>

                {workoutPlanMaterial.file_url ? (
                  <a
                    href={workoutPlanMaterial.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-primary inline-flex items-center space-x-2"
                    aria-label="Download workout plan PDF"
                    tabIndex={0}
                  >
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </a>
                ) : null}
              </div>
            </div>
          )}

          {/* Not-started / no plan yet (edge case) */}
          {workoutPlanState?.status === 'not_started' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <p className="text-sm text-gray-600">
                No workout plan has been generated yet. Your coach will create one for you.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupTrainingPlans;
